<template>
  <div class="multi-filter-input">
    <sui-filter-input v-model="filteredList"
                      :list="emptyList"
                      :rows="2"
                      ref="filter"
                      white-space="pre-wrap"
                      resize="vertical"
                      :show-count="false"
                      :variables="fullVariables">
    </sui-filter-input>
    <collapse class="current-filter" v-model="showFilters">
      <small class="txt-warning" v-if="currentFilter && filterStatus.status !== 2">
        Filtre saisi : <strong>{{ currentFilter }}</strong>
      </small>
      <small class="txt-danger" v-if="currentFilter && filterStatus.status === 2">
        Erreur : {{ filterStatus.error }}
      </small>
      <br>
      <small v-if="appliedFilter !== ''" class="txt-success">
        Filtre appliqué : <strong>{{ appliedFilter }}</strong>
      </small>
    </collapse>
    <div class="btn-toolbar">
      <sui-checkbox v-model="showFilters"
                    type="results" size="sm">
        Afficher les filtres
      </sui-checkbox>
      <btn v-if="hasCSVListener" @click="exportCSV" size="sm">
        <span class="glyphicon glyphicon-download-alt"
              aria-hidden="true">
        </span>
        Export CSV de la sélection
      </btn>
      <btn @click="filteredList.filter = ''" size="sm"
           :disabled="filteredList.filter === ''">
        <span class="glyphicon glyphicon-trash"
              aria-hidden="true">
        </span>
        Effacer
      </btn>
      <btn @click="filterToAll" :disabled="!canApply" size="sm"
           type="warning">
        <span class="glyphicon glyphicon-check"
              aria-hidden="true">
        </span>
        Appliquer
      </btn>
      <btn v-if="helps && helps.length > 0"
           @click="help.modal = !help.modal" type="info" size="sm">
        <span class="glyphicon glyphicon-question-sign"></span>
        Raccourcis
      </btn>
      <btn @click="lock"
           :disabled="filterStatus.status !== 1 || currentFilter === '' || currentFilter !== appliedFilter || updated < data.length"
           type="success" size="sm">
        <span class="glyphicon glyphicon-lock"
              aria-hidden="true">
        </span>
        Figer
      </btn>

      <div class="input-group input-group-sm" v-if="locks.length > 0">
        <strong class="input-group-addon"
           v-for="(l, i) in locks"
           :key="`lock-${i}`">{{ l }}</strong>
        <span class="input-group-btn">
          <btn @click="removeLastLock()">
            <span class="glyphicon glyphicon-remove"
                  aria-hidden="true">
            </span>
          </btn>
        </span>
      </div>
    </div>
    <div class="row">
      <div class="col-xs-12">
        <collapse v-model="help.modal" class="help-collapse">
          <alert type="info">
            <div class="help-cat">
              <btn @click="addFilter(` et `)" size="s">ET</btn>
              <btn @click="addFilter(` ou `)" size="s">OU</btn>
              <btn @click="addFilter(` non `)" size="s">NON</btn>
              <btn @click="addFilter(` ( `)" size="s">(</btn>
              <btn @click="addFilter(` ) `)" size="s">)</btn>
              <div class="input-group editor">
                <input type="text" class="form-control"
                       v-model.trim="help.filter"
                       aria-describedby="clear-addon"
                       placeholder="Filtrer les boutons..."
                       @keydown.enter.exact.prevent>
                <span class="input-group-btn">
                  <btn type="warning" id="clear-addon" @click="help.filter = ''">Effacer</btn>
                </span>
              </div>
            </div>

            <div v-for="(h, i) in help.list" :key="`help-cat-${i}`">
              <h4 v-html="h.title"></h4>
              <div class="help-cat">
                <span v-for="(b, j) in h.list"
                      class="help-cat-btn"
                      v-if="help.filter === '' || b.t.toLowerCase().indexOf(help.filter.toLowerCase()) !== -1"
                      :key="`help-cat-${i}-btn-${j}`">
                  <btn @click="addFilter(b.f)" size="xs">
                    {{ b.t }}
                  </btn>
                </span>
              </div>
            </div>
          </alert>
        </collapse>
      </div>
    </div>

    <div v-for="(elt, i) in data" :key="`filter-${i}`" v-show="false">
      <sui-filter-input v-model="elt.filteredList"
                        :list="elt.list"
                        :variables="elt.variables"
                        @input="subfilterChanged">
      </sui-filter-input>
    </div>
  </div>
</template>

<script>
import suiFilterInput from '@/sui/components/filterInput'
import suiCheckbox from '@/sui/components/checkbox'
import ice from '@/sui/tools/ice'

function toData (list) {
  const result = []

  for (let i = 0; i !== list.length; ++i) {
    const l = list[i]

    result.push(
      {
        filteredList: { filter: '', list: [], status: 0 },
        list: ice.freeze(l.list),
        stack: [],
        variables: l.variables
      }
    )
  }
  return result
}

export default {
  name: 'multi-filter-input',
  props: {
    list: {
      type: Array,
      required: true
    },

    value: {
      type: String,
      required: true
    },

    helps: {
      type: Array,
      default: () => []
    }
  },

  data: function () {
    return {
      locks: [],
      showFilters: true,
      appliedFilter: '',
      changed: false,
      updated: 0,
      filteredList: { filter: '', list: [], status: 0 },
      emptyList: [],
      data: toData(this.list),
      help: {
        filter: '',
        modal: false,
        list: this.helps
      }
    }
  },

  watch: {
    helps: function (val) {
      this.help.list = val
    },

    list: function (val) {
      this.data = toData(val)
      this.changed = true
    },

    value: function (f) {
      this.filteredList.filter = f
    },

    'filteredList.filter': function (f) {
      this.changed = true
      this.$emit('input', f)
    },

    data: function () {
      this.filterToAll()
    }
  },

  computed: {
    hasCSVListener: function () {
      return this.$listeners && this.$listeners.csv
    },

    fullVariables: function () {
      const result = []
      const keys = {}
      for (let i = 0; i !== this.data.length; ++i) {
        const d = this.data[i]
        const fvs = d.variables
        for (let j = 0; j !== fvs.length; ++j) {
          const fv = fvs[j]
          if (keys[fv.key] == null) {
            keys[fv.key] = true
            result.push(fv)
          }
        }
      }

      return result
    },

    filterStatus: function () {
      return {
        status: this.filteredList.status,
        error: this.filteredList.error
      }
    },

    canApply: function () {
      return this.filteredList.status !== 2 && this.changed && this.appliedFilter !== this.currentFilter
    },

    currentFilter: function () {
      const f = this.filteredList.filter

      let balanced = false
      let level = 0
      for (let i = 0; i !== f.length; ++i) {
        const char = f[i]
        if (level === 0) {
          if (char !== ' ' && char !== '(') {
            balanced = false
            break
          }

          level = char === '(' ? 1 : 0
          continue
        }

        if (char === '(') {
          level += 1
          if (balanced) {
            balanced = false
            break
          }
        } else if (char === ')') {
          level -= 1
          balanced = level === 0
        }
      }

      if (this.locks.length === 0) {
        if (f === '') {
          return ''
        }

        return balanced ? f : `(${f})`
      }

      const prefix = this.locks.join(' et ')
      if (f === '') {
        return prefix
      }

      const expr = balanced ? f : `(${f})`

      return `${prefix} et ${expr}`
    }
  },

  methods: {
    addFilter: function (f) {
      const start = this.$refs.filter.$refs.filter.selectionStart
      const end = this.$refs.filter.$refs.filter.selectionEnd

      const filter = this.filteredList.filter

      const apply = start === 0 ? f : `${filter.slice(0, start)}${f}${filter.slice(end)}`
      this.$set(this.filteredList, 'filter', apply)
      this.help.filter = ''
    },

    exportCSV: function () {
      this.emitList('csv')
    },

    lock: function () {
      this.locks.push(`(${this.filteredList.filter})`)
      for (let i = 0; i !== this.data.length; ++i) {
        const d = this.data[i]
        d.stack.push(d.list)
        d.list = ice.freeze(d.filteredList.list.slice())
      }
      this.filteredList.filter = ''
      this.emitList()
    },

    removeLastLock: function () {
      const last = this.locks.pop()
      this.filteredList.filter = this.filteredList.filter === ''
        ? last : `${last} et ${this.filteredList.filter}`

      for (let i = 0; i !== this.data.length; ++i) {
        const d = this.data[i]
        d.list = d.stack.pop()
      }
      this.emitList()
    },

    filterToAll: function () {
      this.changed = false
      this.updated = 0
      this.appliedFilter = this.currentFilter

      for (let i = 0; i !== this.data.length; ++i) {
        const d = this.data[i]
        d.filteredList.filter = this.currentFilter
      }
    },

    subfilterChanged: function () {
      this.updated++

      if (this.updated === this.data.length) {
        this.emitList()
      }
    },

    emitList: function (event) {
      const result = []
      const initial = []
      for (let i = 0; i !== this.data.length; ++i) {
        const d = this.data[i]
        result.push(d.filteredList.list)
        initial.push(d.list)
      }
      const eventName = event == null ? 'filter' : event
      const eventData = {
        filter: this.appliedFilter,
        lists: result,
        data: initial
      }
      this.$emit(eventName, eventData)
    }
  },
  components: {
    'sui-filter-input': suiFilterInput,
    'sui-checkbox': suiCheckbox
  }
}
</script>

<style scoped lang="less">
@import '~variables';

.form-group {
  margin-bottom: 0rem;
}

.input-group {
  width: fit-content;
}

.current-filter {
  margin-bottom: 0.5rem;
}

.help-cat {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.help-bar {
  margin-bottom: 0.2rem;
}

.help-collapse {
  .alert {
    margin-top: 0.2rem;
    margin-bottom: 0.2rem;
  }

  h4 {
    margin-top: 1rem;
    margin-bottom: 0.3rem;
  }
}

.help-cat-btn {

}

.editor {
  width: 100%;
}

.btn-toolbar {
  margin-top: 0.2rem;

  .c-checkbox {
    float: left;
    margin-left: 5px;
    line-height: 1.5rem;
  }
}
</style>
